import { Controller } from "@hotwired/stimulus"
import { Calendar } from "vanilla-calendar-pro"
// CSS is imported in build/stylesheets/application.tailwind.css

export default class extends Controller {
  static targets = ["input", "display", "trigger", "popup", "clearBtn", "icon", "hour", "minute"]
  static values = {
    locale: { type: String, default: "es" },
    placeholder: { type: String, default: "Seleccionar fecha y hora" },
    disablePast: { type: Boolean, default: false }
  }

  connect() {
    this.calendar = null
    this.isOpen = false
    this.createPopup()
    this.setupCalendar()
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.boundHandleOutsideClick)
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
    }
    document.removeEventListener("click", this.boundHandleOutsideClick)
  }

  createPopup() {
    // Create popup container if it doesn't exist
    if (!this.hasPopupTarget) {
      const popup = document.createElement("div")
      popup.setAttribute("data-datetimepicker-target", "popup")
      popup.className = "datepicker-popup"
      popup.style.display = "none"
      this.element.appendChild(popup)
    }
  }

  setupCalendar() {
    // Parse initial datetime value
    let initialDate = null

    if (this.inputTarget.value) {
      const dt = new Date(this.inputTarget.value)
      if (!isNaN(dt.getTime())) {
        initialDate = dt.toISOString().split('T')[0]
      }
    }

    const controller = this
    this.lastSelectedDate = initialDate

    this.calendar = new Calendar(this.popupTarget, {
      locale: this.localeValue,
      selectionDatesMode: "single",
      selectedDates: initialDate ? [initialDate] : [],
      disableDatesPast: this.disablePastValue,
      selectedTheme: "light",
      enableDateToggle: false,
      onClickDate(self, event) {
        // Get clicked date from the DOM element
        const dateEl = event.target.closest("[data-vc-date]")
        if (dateEl) {
          const clickedDate = dateEl.getAttribute("data-vc-date")
          if (clickedDate) {
            controller.lastSelectedDate = clickedDate
            controller.updateValue()
            controller.close()
          }
        }
      }
    })

    this.calendar.init()

    // Ensure calendar starts hidden
    this.close()

    if (initialDate) {
      this.updateDisplay()
    }
  }

  updateTime() {
    if (this.lastSelectedDate) {
      this.updateValue()
    }
  }

  updateValue() {
    if (!this.lastSelectedDate) return

    const hour = this.hasHourTarget ? this.hourTarget.value : "09"
    const minute = this.hasMinuteTarget ? this.minuteTarget.value : "00"
    const datetimeStr = `${this.lastSelectedDate}T${hour}:${minute}`
    this.inputTarget.value = datetimeStr
    this.updateDisplay()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.popupTarget.style.display = "block"
    this.isOpen = true
  }

  close() {
    this.popupTarget.style.display = "none"
    this.isOpen = false
  }

  handleOutsideClick(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.close()
    }
  }

  clear(event) {
    event.preventDefault()
    event.stopPropagation()

    this.inputTarget.value = ""
    this.lastSelectedDate = null

    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = this.placeholderValue
      this.displayTarget.classList.add("text-gray-400")
      this.displayTarget.classList.remove("text-gray-900")
    }

    // Hide clear button, show calendar icon
    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.classList.add("hidden")
    }
    if (this.hasIconTarget) {
      this.iconTarget.classList.remove("hidden")
    }

    // Reset hour/minute selects
    if (this.hasHourTarget) this.hourTarget.value = "09"
    if (this.hasMinuteTarget) this.minuteTarget.value = "00"

    if (this.calendar) {
      this.calendar.selectedDates = []
      this.calendar.update()
    }

    this.close()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  updateDisplay() {
    if (!this.hasDisplayTarget || !this.inputTarget.value) return

    const dt = new Date(this.inputTarget.value)
    if (isNaN(dt.getTime())) return

    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }
    const formattedDate = dt.toLocaleDateString(this.localeValue, options)

    this.displayTarget.textContent = formattedDate
    this.displayTarget.classList.remove("text-gray-400")
    this.displayTarget.classList.add("text-gray-900")

    // Show clear button, hide calendar icon
    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.classList.remove("hidden")
    }
    if (this.hasIconTarget) {
      this.iconTarget.classList.add("hidden")
    }
  }
}
